/**
 * Source Archive — owner-only download portal.
 *
 * UX
 *   - Top: shows which zip is currently available (filename, size, last
 *     modified). One click on "Generate one-time link" mints a fresh
 *     nonce, copies the URL to clipboard, and opens it in a new tab so
 *     the download starts immediately and the nonce burns on first use.
 *   - Bottom: 50-row audit feed (who minted, when, expired vs used,
 *     redeem IP). Nonces are truncated to the first 8 chars for display
 *     so a screen-recording of this page can't leak a usable token.
 *
 * Authorization (defense-in-depth)
 *   - The router (App.js) wraps the route in `<OwnerOnlyRoute>` which
 *     hides it from anyone whose email isn't in the owner allowlist.
 *   - Every backend endpoint independently re-checks via `_require_owner`
 *     so client-side bypass is impossible — the worst a non-owner can do
 *     is render a panel that 403s on every call.
 *   - We never echo the full nonce back into the DOM after copying it.
 *     The nonce only lives on the clipboard and in the new-tab URL.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Lock, Download, ArrowsClockwise, Copy, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

function formatBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function SourceArchivePage() {
  const [info, setInfo] = useState(null);
  const [audit, setAudit] = useState([]);
  const [minting, setMinting] = useState(false);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/downloads/codebase/archive-info"),
        api.get("/downloads/codebase/audit"),
      ]);
      setInfo(a.data);
      setAudit(b.data || []);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error("Failed to load source archive panel");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mintAndOpen = async () => {
    setMinting(true);
    try {
      const res = await api.post("/downloads/codebase/mint-nonce", {});
      const url = res.data?.url;
      if (!url) throw new Error("no url");
      // Try clipboard — best effort. The link is single-use so it's
      // not the end of the world if the copy fails.
      try {
        await navigator.clipboard.writeText(url);
        toast.success("One-time link copied to clipboard. Opening download…");
      } catch {
        toast.success("Link minted. Opening download…");
      }
      // Open in a new tab so the current admin session stays put.
      window.open(url, "_blank", "noopener,noreferrer");
      // Refresh the audit feed so the new mint shows up.
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mint link");
    } finally { setMinting(false); }
  };

  if (denied) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="source-archive-denied">
        <Lock size={36} weight="duotone" className="mx-auto text-[#A3A3A3] mb-3" />
        <h1 className="text-xl font-black tracking-tight text-[#0A0A0A]">Not authorized</h1>
        <p className="text-sm text-[#525252] mt-2">
          The source archive is restricted to the product owner. If you believe this is an
          error, ask the owner to add your email to <code className="bg-[#F5F5F5] px-1.5 py-0.5">CODEBASE_DOWNLOAD_OWNER_EMAILS</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="source-archive-page">
      <div className="flex items-center gap-3 mb-1">
        <Lock size={20} weight="duotone" className="text-[#0055FF]" />
        <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Source Archive
        </h1>
      </div>
      <p className="text-sm text-[#525252] mb-6">
        Owner-only one-time-burn download portal for the Eyvo Pulse codebase.
        Every link is single-use and expires in 10 minutes; redemption IP and time are recorded below for audit.
      </p>

      {/* Available archive card */}
      <div className="border border-[#E5E5E5] bg-white p-5 mb-6" data-testid="archive-info-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3]">Currently published</p>
            {loading ? (
              <p className="text-sm text-[#A3A3A3] mt-2">Loading…</p>
            ) : info?.available ? (
              <>
                <p className="text-lg font-black tracking-tight text-[#0A0A0A] mt-1 break-all" data-testid="archive-filename">{info.filename}</p>
                <p className="text-xs text-[#525252] mt-1">
                  <span className="font-semibold">{formatBytes(info.size_bytes)}</span>
                  <span className="text-[#A3A3A3]"> · published {formatTime(info.modified_at)}</span>
                </p>
              </>
            ) : (
              <p className="text-sm text-[#D97706] mt-2 inline-flex items-center gap-1.5">
                <Warning size={14} weight="bold" /> No source archive published yet — drop a zip into <code className="bg-[#F5F5F5] px-1.5 py-0.5 mx-1">/app/downloads/</code>.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 h-9 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider hover:bg-[#FAFAFA] disabled:opacity-40"
              data-testid="refresh-btn"
            >
              <ArrowsClockwise size={12} weight="bold" /> Refresh
            </button>
            <button
              onClick={mintAndOpen}
              disabled={minting || loading || !info?.available}
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-40"
              data-testid="mint-link-btn"
            >
              <Download size={12} weight="bold" /> {minting ? "Minting…" : "Generate one-time link"}
            </button>
          </div>
        </div>
      </div>

      {/* Audit feed */}
      <div className="border border-[#E5E5E5] bg-white" data-testid="audit-card">
        <div className="px-5 py-3 border-b border-[#F5F5F5] flex items-center justify-between">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#525252]">Audit feed (last 50)</h2>
          <span className="text-[10px] text-[#A3A3A3]">{audit.length} mint(s)</span>
        </div>
        {audit.length === 0 ? (
          <p className="px-5 py-8 text-center text-xs text-[#A3A3A3]" data-testid="audit-empty">
            No download links have been minted yet.
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="bg-[#FAFAFA] text-[9px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-4 py-2 font-bold">Nonce</th>
                <th className="text-left px-3 py-2 font-bold">Minted by</th>
                <th className="text-left px-3 py-2 font-bold">Minted at</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Redeemed at</th>
                <th className="text-left px-4 py-2 font-bold">From IP</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((r) => {
                const used = !!r.used_at;
                const expired = !used && r.expires_at && new Date(r.expires_at) <= new Date();
                return (
                  <tr key={r.nonce_prefix + r.minted_at} className="border-t border-[#F5F5F5]">
                    <td className="px-4 py-2 font-mono text-[#525252]">{r.nonce_prefix}…</td>
                    <td className="px-3 py-2">{r.minted_by_email || "—"}</td>
                    <td className="px-3 py-2 text-[#525252]">{formatTime(r.minted_at)}</td>
                    <td className="px-3 py-2">
                      {used ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#F0FDF4] text-[#16A34A]">Used</span>
                      ) : expired ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FEF2F2] text-[#B91C1C]">Expired</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FFFBEB] text-[#D97706]">Pending</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#525252]">{used ? formatTime(r.used_at) : "—"}</td>
                    <td className="px-4 py-2 font-mono text-[#525252]">{r.used_by_ip || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[10px] text-[#A3A3A3] mt-4 inline-flex items-center gap-1.5">
        <Copy size={10} /> Tip: The "Generate one-time link" button copies the URL to your clipboard
        AND opens the download immediately — so the link burns on its first use and you don't have to paste it anywhere.
      </p>
    </div>
  );
}
